/**
 * Bay / Date / Time / Duration rows card — [BUT-843, ours].
 *
 * The instructor mini-card, a hairline divider, and the four label/value
 * rows from Figma `14549:734858`. Every value arrives pre-formatted — this
 * card does no date math. Callers format with `@butter/library/datetime`'s
 * `formatClubDate` / `formatClubTime` (`'EEEE, d MMMM, yyyy'`, the format
 * `BookingConfirmationPage` and `DemoTicketCard` both use) and
 * `~/services/internalApi/lessons`' `formatLessonHours` for the duration —
 * reused rather than re-implemented.
 *
 * @module pages/lessons/components/LessonDetailsCard
 */

import type { ImageData } from '@lgc/database/types/domains/storage';
import { InstructorIdentity } from '@butter/ui/domains/lessons';

export interface LessonDetailsCardProps {
  instructorName: string;
  instructorImage?: ImageData | null;
  /** True when `instructorName` is an unresolved placeholder, not a real instructor. */
  instructorUnresolved?: boolean;
  bayName: string;
  dateLabel: string;
  /** Shorter phone rendering of `dateLabel` ("EEE, d MMM, yyyy"); falls back to `dateLabel` when absent. */
  dateLabelCompact?: string;
  timeRangeLabel: string;
  durationLabel: string;
  className?: string;
}

export function LessonDetailsCard({
  instructorName,
  instructorImage,
  instructorUnresolved = false,
  bayName,
  dateLabel,
  dateLabelCompact,
  timeRangeLabel,
  durationLabel,
  className,
}: LessonDetailsCardProps) {
  return (
    <div
      className={`rounded-3xl border-1.25 border-theme-100 p-6 md:rounded-28 md:p-7${className ? ` ${className}` : ''}`}
      data-testid="lesson-details-card"
    >
      <InstructorIdentity
        name={instructorName}
        image={instructorImage}
        unresolved={instructorUnresolved}
        variant="compact"
      />

      <div className="my-5 h-px w-full bg-butter-gray-200 md:my-6" role="separator" aria-hidden />

      <dl className="space-y-4 md:space-y-3">
        <div className="flex items-center justify-between gap-4">
          <dt className="text-base leading-5 font-medium text-fg-tertiary">Bay</dt>
          <dd className="text-base leading-5 font-semibold text-fg-primary" data-testid="lesson-details-bay">
            {bayName}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-base leading-5 font-medium text-fg-tertiary">Date</dt>
          <dd className="text-base leading-5 font-semibold text-fg-primary" data-testid="lesson-details-date">
            {dateLabelCompact ? (
              <>
                <span className="md:hidden">{dateLabelCompact}</span>
                <span className="hidden md:inline">{dateLabel}</span>
              </>
            ) : (
              dateLabel
            )}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-base leading-5 font-medium text-fg-tertiary">Time</dt>
          <dd className="text-base leading-5 font-semibold text-fg-primary" data-testid="lesson-details-time">
            {timeRangeLabel}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-base leading-5 font-medium text-fg-tertiary">Duration</dt>
          <dd className="text-base leading-5 font-semibold text-fg-primary" data-testid="lesson-details-duration">
            {durationLabel}
          </dd>
        </div>
      </dl>
    </div>
  );
}
